/**
 * « Modèles des thèmes (v2) » — the admin sees EVERY model a theme uses
 * (design system v2: simple, décoré, special pages), in one of its colourways,
 * drawn by ThemedPageView exactly as it prints (same layers as the PDF).
 * The models live in code (worker lib/themes/catalog), not in layout_templates:
 * the pages come from GET /settings/theme-catalog, expanded by the worker with
 * STAND-IN photos (grey placeholders with a silhouette, drawn here) and sample
 * words — never a client's photo or words.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { settingsApi, type ThemeCatalogModel, type ThemeCatalogPage, type ThemeCatalogPhoto, type ThemeCatalogResponse } from '@/lib/api';
import { ThemedPageView } from '@/components/page/ThemedPageView';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { BarGroup, ColourwayDots, ThemeTabs } from '@/components/theme/ThemeBar';

/**
 * A neutral placeholder photo of the stand-in's exact size (its width/height
 * drive the crop, as a real photo's would): soft grey, a subtle person
 * silhouette where the stand-in's face is. Inline SVG data URL — no client photo.
 */
export function placeholderPhotoUrl(p: Pick<ThemeCatalogPhoto, 'width' | 'height' | 'faces'>): string {
  const w = Math.max(1, Math.round(p.width)), h = Math.max(1, Math.round(p.height));
  const f = p.faces?.[0] ?? { x: w / 2 - w * 0.06, y: h * 0.3, width: w * 0.12, height: w * 0.15 };
  const cx = f.x + f.width / 2, cy = f.y + f.height / 2;
  const r = f.width * 0.55;
  // shoulders: a dome from just under the head down past the photo's bottom edge
  const top = f.y + f.height * 1.12, rx = f.width * 1.75, k = rx * 0.7;
  const body = `M${cx - rx} ${h + 1}L${cx - rx} ${top + k}Q${cx - rx} ${top} ${cx} ${top}Q${cx + rx} ${top} ${cx + rx} ${top + k}L${cx + rx} ${h + 1}Z`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`
    + `<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#E8EAED"/><stop offset="1" stop-color="#D8DCE1"/></linearGradient></defs>`
    + `<rect width="${w}" height="${h}" fill="url(#g)"/>`
    + `<g fill="#CBD0D6"><circle cx="${cx}" cy="${cy}" r="${r}"/><path d="${body}"/></g></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const KIND_LABEL: Record<ThemeCatalogModel['kind'], string> = { simple: 'simple', decorated: 'décoré', special: 'page spéciale' };

/** Does a model draw TINTED pieces (worker themes/tint.ts — 4-10 « Collage anniversaire »): its colour can be chosen. */
export function tintedModel(m: Pick<ThemeCatalogModel, 'pages'>): boolean {
  return m.pages.some((p) => !!p.background?.tint || p.elements.some((e) => Array.isArray(e?.tint)));
}
/** Colours to try a tinted design in — the admin can also pick any colour (« Couleur du thème » on the order page). */
export const TINT_SWATCHES = ['#E2725B', '#D9468B', '#9C3F8F', '#6B4FA0', '#2F5D73', '#3E7C59', '#C9A04C', '#1F2F4A'] as const;

/** One page of a model, at the card's width: the page box ThemedPageView fills. */
function PageBox({ page, photos, className }: { page: ThemeCatalogPage; photos: ThemeCatalogPhoto[]; className?: string }) {
  const urls = useMemo(() => new Map(photos.map((p) => [p.id, placeholderPhotoUrl(p)])), [photos]);
  return (
    <div
      className={cn('relative overflow-hidden bg-white', className)}
      style={{ aspectRatio: '200 / 297' }}
      // A theme whose pieces are not in the decor bucket yet: its spots stay EMPTY
      // here (as « not made yet » prints them) instead of a broken-image icon.
      onErrorCapture={(e) => { const el = e.target as HTMLElement; if (el.tagName === 'IMG') el.style.visibility = 'hidden'; }}
    >
      <ThemedPageView page={page} photoUrl={(id) => urls.get(id) ?? null} />
    </div>
  );
}

function Chip({ children, tone = 'gray' }: { children: ReactNode; tone?: 'gray' | 'gold' | 'blue' | 'violet' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    gold: 'bg-amber-50 text-amber-800',
    blue: 'bg-sky-50 text-sky-800',
    violet: 'bg-violet-50 text-violet-800',
  } as const;
  return <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium leading-none whitespace-nowrap', tones[tone])}>{children}</span>;
}

interface Card { key: string; model: ThemeCatalogModel; pages: ThemeCatalogPage[]; noPhoto: boolean }

function ModelCard({ card, onOpen }: { card: Card; onOpen: () => void }) {
  const { t } = useT();
  const m = card.model;
  const spread = card.pages.length === 2;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-theme-model={m.code}
      // ~50 full pages of layers: only the cards near the screen are laid out and painted.
      style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 420px' }}
      className={cn('group text-left bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow min-w-0',
        spread && 'col-span-2')}
    >
      <div className="relative bg-gray-100 p-2" data-adm-media>
        {card.pages.length === 0 ? (
          <div className="flex items-center justify-center text-xs text-gray-400" style={{ aspectRatio: '200 / 297' }}>
            {t('Aperçu indisponible')}
          </div>
        ) : (
          <div className={cn('grid gap-0 shadow-sm', spread ? 'grid-cols-2' : 'grid-cols-1')}>
            {card.pages.map((p, i) => <PageBox key={i} page={p} photos={m.photos} />)}
          </div>
        )}
        <span className="absolute top-3 left-3 z-[2000] px-1.5 py-0.5 rounded bg-white/85 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {t('Exemple')}
        </span>
      </div>
      <div className="px-3 py-2.5 space-y-1.5 min-w-0">
        <p className="text-sm text-gray-900 leading-snug min-w-0">
          <span className="font-mono font-bold mr-1.5">{m.code}</span>
          <span className="break-words">{t(m.name)}{card.noPhoto ? ` · ${t('sans photo')}` : ''}</span>
        </p>
        <div className="flex flex-wrap gap-1">
          <Chip tone={m.kind === 'decorated' ? 'gold' : m.kind === 'special' ? 'violet' : 'gray'}>{t(KIND_LABEL[m.kind])}</Chip>
          <Chip>{t('style {s}', { s: m.style })}</Chip>
          {m.minAlbumPages ? <Chip tone="blue">{t('{n}+ pages', { n: m.minAlbumPages })}</Chip> : null}
          {spread ? <Chip tone="blue">{t('double page')}</Chip> : null}
          {m.favourite ? <Chip tone="gold">★ {t('favori')}</Chip> : null}
          {tintedModel(m) ? <Chip tone="violet">{t('couleur au choix')}</Chip> : null}
        </div>
      </div>
    </button>
  );
}

/** The page big, on top of everything (click anywhere or Échap to close). */
function Lightbox({ card, onClose }: { card: Card; onClose: () => void }) {
  const { t } = useT();
  const spread = card.pages.length === 2;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/70 flex flex-col items-center justify-center p-4"
      onClick={onClose}
    >
      <div className={cn('grid shadow-2xl', spread ? 'grid-cols-2' : 'grid-cols-1')}
        style={{ height: 'min(82vh, 1100px)', aspectRatio: spread ? '400 / 297' : '200 / 297', maxWidth: '100%' }}>
        {card.pages.map((p, i) => <PageBox key={i} page={p} photos={card.model.photos} className="h-full" />)}
      </div>
      <p className="mt-3 text-sm text-white/90 text-center">
        <span className="font-mono font-bold mr-2">{card.model.code}</span>{t(card.model.name)}
        {card.noPhoto ? ` · ${t('sans photo')}` : ''} — {t('exemple (photos et textes fictifs)')}
      </p>
    </div>
  );
}

export function ThemeModelsGalleryView({ data, theme, colourway, onTheme, onColourway, loading, accent = null, onAccent, bars }: {
  data: ThemeCatalogResponse | undefined;
  theme: string;
  colourway: string | null;
  onTheme: (slug: string) => void;
  onColourway: (id: string) => void;
  loading?: boolean;
  /** The colour the tinted designs are previewed in (null = the colourway's own). */
  accent?: string | null;
  onAccent?: (hex: string | null) => void;
  /** The global defaults (« Pages avec modèle », « Modèles différents »), drawn in the same bar. */
  bars?: ReactNode;
}) {
  const { t } = useT();
  const [open, setOpen] = useState<Card | null>(null);
  const current = data?.themes.find((x) => x.slug === theme);
  const cards = useMemo(() => {
    const out: Record<ThemeCatalogModel['kind'], Card[]> = { simple: [], decorated: [], special: [] };
    // Keyed by theme + colourway too: a new theme remounts its pages (an image hidden
    // because the last theme's piece was missing never stays hidden).
    const at = `${data?.theme.slug}|${data?.colourway.id}|${data?.accentOverride ?? ''}`;
    for (const m of data?.models ?? []) {
      out[m.kind].push({ key: `${at}|${m.code}`, model: m, pages: m.pages, noPhoto: false });
      if (m.noPhotoPage) out[m.kind].push({ key: `${at}|${m.code}-np`, model: m, pages: [m.noPhotoPage], noPhoto: true });
    }
    return out;
  }, [data]);
  const sections: Array<{ kind: ThemeCatalogModel['kind']; title: string; hint: string }> = [
    { kind: 'decorated', title: t('Pages décorées'), hint: t('Les modèles que le thème peut choisir pour décorer une page (« Pages avec modèle »).') },
    { kind: 'simple', title: t('Pages simples'), hint: t('Les autres pages : papier, polices et légendes du thème.') },
    { kind: 'special', title: t('Pages spéciales'), hint: t('Ouverture, section, dédicace, fin, panorama — toujours dans le thème.') },
  ];
  const total = (data?.counts.simple ?? 0) + (data?.counts.decorated ?? 0) + (data?.counts.special ?? 0);
  const activeCw = colourway ?? data?.colourway.id ?? null;

  return (
    <div data-theme-gallery>
      {/* THE BAR (same pieces as the album's « Thème de l'album » bar): theme tabs with their
          active/inactive dot, then the coloris, the colour to preview, and the global defaults. */}
      <div className="rounded-xl border border-adm-line bg-adm-surface shadow-sm mb-3" data-theme-header>
        <div className="flex items-center gap-3 flex-wrap px-3 pt-3 pb-2">
          <span className="text-[13px] font-semibold text-adm-ink">{t('Thèmes')}</span>
          <ThemeTabs
            themes={data?.themes ?? [{ slug: theme, name: theme, on: false }]}
            value={theme}
            onChange={onTheme}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-adm-line px-3 py-2 text-xs">
          {current && current.colourways.length > 0 && (
            <BarGroup label={t('Coloris')}>
              <ColourwayDots labels items={current.colourways} value={activeCw} onChange={(id) => id && onColourway(id)} />
            </BarGroup>
          )}
          {/* « Couleur du thème »: the tinted designs (4-10 Collage anniversaire…) previewed in any colour */}
          {onAccent && (data?.models ?? []).some(tintedModel) && (
            <BarGroup label={t('Couleur du thème')} title={t('lettres, rubans, washi, pierres et papier — choisie par album sur la page commande')}>
              <span className="inline-flex items-center gap-1 flex-wrap" data-theme-tint>
                <button type="button" onClick={() => onAccent(null)}
                  className={cn('px-2 py-0.5 rounded-full border text-[11px]', !accent ? 'border-brand-500 ring-1 ring-brand-500 bg-adm-surface text-adm-ink' : 'border-adm-line bg-adm-surface text-adm-muted hover:bg-adm-surface-3')}>
                  {t('du coloris')}
                </button>
                {TINT_SWATCHES.map((c) => (
                  <button key={c} type="button" aria-label={c} title={c} onClick={() => onAccent(c)}
                    className={cn('w-5 h-5 rounded-full border border-black/10', accent?.toUpperCase() === c && 'ring-2 ring-offset-1 ring-brand-500')}
                    style={{ background: c }} />
                ))}
                <label className="relative inline-flex w-5 h-5 rounded-full border border-black/15 cursor-pointer" title={t('Autre couleur')}
                  style={{ background: 'conic-gradient(#E2725B, #C9A04C, #3E7C59, #2F5D73, #6B4FA0, #D9468B, #E2725B)' }}>
                  <input type="color" aria-label={t('Autre couleur')} className="absolute inset-0 opacity-0 cursor-pointer"
                    value={accent ?? data?.colourway.accent ?? '#E2725B'} onChange={(e) => onAccent(e.target.value.toUpperCase())} />
                </label>
              </span>
            </BarGroup>
          )}
          <span className="flex flex-wrap items-center gap-x-4 gap-y-2 lg:ml-auto">
            <span className="inline-flex items-center gap-2 text-[11px] text-adm-muted">
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{t('actif')}</span>
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-gray-300" />{t('inactif')}</span>
            </span>
            {bars}
          </span>
        </div>
      </div>

      {/* Notes: examples; pieces not uploaded yet; inactive */}
      <div className="space-y-2 mb-4">
        <p className="text-xs text-gray-500">
          {t('Chaque page est rendue exactement comme à l’impression. Photos (silhouettes grises) et textes (« {titre} », « {sous} »…) sont des exemples, jamais ceux d’une cliente.',
            { titre: String(data?.facts.titre ?? 'Valencia 2026'), sous: String(data?.facts.sous_titre ?? 'Souvenir de voyage') })}
        </p>
        {current && !current.piecesOnline && (
          <div className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2">
            {t('Les pièces de ce thème (papiers, objets, cadres dessinés) ne sont pas encore en ligne : les emplacements restent vides et le papier s’affiche en couleur unie jusqu’à leur mise en ligne.')}
          </div>
        )}
        {current && !current.on && (
          <div className="text-xs rounded-lg border border-gray-200 bg-gray-50 text-gray-600 px-3 py-2">
            {data?.themesSwitch
              ? t('Thème inactif : aucun album ne le reçoit pour l’instant (il n’est pas dans la liste des thèmes activés).')
              : t('Thèmes désactivés : aucun album ne reçoit de thème pour l’instant (interrupteur général éteint).')}
          </div>
        )}
      </div>

      {loading && !data ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-[200/297] bg-white rounded-xl border border-gray-100 animate-pulse" />)}
        </div>
      ) : (
        sections.map((s) => cards[s.kind].length > 0 && (
          <section key={s.kind} className="mb-6">
            <div className="flex items-baseline gap-2 mb-1 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-900">{s.title}</h3>
              <span className="text-xs text-gray-400 tabular-nums">{data?.counts[s.kind] ?? 0}</span>
            </div>
            <p className="text-xs text-gray-400 mb-2">{s.hint}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {cards[s.kind].map((c) => <ModelCard key={c.key} card={c} onOpen={() => setOpen(c)} />)}
            </div>
          </section>
        ))
      )}
      {data && total === 0 && <p className="text-sm text-gray-500">{t('Aucun modèle pour ce thème.')}</p>}
      {open && <Lightbox card={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** The page section: fetches the theme's models (cached by react-query) and draws them. */
export function ThemeModelsGallery({ onCount, bars }: { onCount?: (n: number, theme: string) => void; bars?: ReactNode }) {
  const [theme, setTheme] = useState('voyage');
  const [colourway, setColourway] = useState<string | null>(null);
  const [accent, setAccent] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['theme-catalog', theme, colourway, accent],
    queryFn: () => settingsApi.themeCatalog(theme, colourway, accent),
    staleTime: 10 * 60_000,
    placeholderData: (prev) => prev,
  });
  const data = q.data;
  const n = data ? data.models.length : 0;
  useEffect(() => { if (data && onCount) onCount(n, data.theme.name); }, [data, n, onCount]);
  return (
    <>
      {q.error && <p className="text-sm text-red-600 mb-3">{(q.error as Error).message}</p>}
      <div className={cn('transition-opacity', q.isPlaceholderData && q.isFetching && 'opacity-50')}>
      <ThemeModelsGalleryView
        data={data}
        theme={theme}
        colourway={colourway}
        loading={q.isLoading}
        onTheme={(s) => { setTheme(s); setColourway(null); setAccent(null); }}
        onColourway={setColourway}
        accent={accent}
        onAccent={setAccent}
        bars={bars}
      />
      </div>
    </>
  );
}
